import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import type { RootState } from "../store";
import {
  getNotifications,
  markAsRead,
  markAllAsRead,
  deleteNotification,
  addNotification,
} from "../mocks/notifications";
import { useAppSelector } from "@/store/hooks";

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export const useNotifications = () => {
  const { user } = useAppSelector((state) => state.auth);

  const query = useQuery({
    queryKey: ["notifications", user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      await delay(300);
      return getNotifications(user.id);
    },
    enabled: !!user?.id,
    refetchInterval: 10000,
  });

  const unreadCount = query.data?.filter((n) => !n.read).length || 0;

  return {
    notifications: query.data || [],
    unreadCount,
    isLoading: query.isLoading,
    refetch: query.refetch,
  };
};

export const useMarkNotificationAsRead = () => {
  const queryClient = useQueryClient();
  const { user } = useSelector((state: RootState) => state.auth);

  return useMutation({
    mutationFn: async (notificationId: string) => {
      if (!user?.id) throw new Error("Not authenticated");
      await delay(200);
      markAsRead(user.id, notificationId);
      return { success: true };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications", user?.id] });
    },
  });
};

export const useMarkAllNotificationsAsRead = () => {
  const queryClient = useQueryClient();
  const { user } = useSelector((state: RootState) => state.auth);

  return useMutation({
    mutationFn: async () => {
      if (!user?.id) throw new Error("Not authenticated");
      await delay(300);
      markAllAsRead(user.id);
      return { success: true };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications", user?.id] });
    },
  });
};

export const useDeleteNotification = () => {
  const queryClient = useQueryClient();
  const { user } = useSelector((state: RootState) => state.auth);

  return useMutation({
    mutationFn: async (notificationId: string) => {
      if (!user?.id) throw new Error("Not authenticated");
      await delay(200);
      deleteNotification(user.id, notificationId);
      return { success: true };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications", user?.id] });
    },
  });
};

export const useCreateNotification = () => {
  const queryClient = useQueryClient();
  const { user } = useSelector((state: RootState) => state.auth);

  return useMutation({
    mutationFn: async (notification: Parameters<typeof addNotification>[1]) => {
      if (!user?.id) throw new Error("Not authenticated");
      await delay(200);
      return addNotification(user.id, notification);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications", user?.id] });
    },
  });
};
